import { all, select, takeEvery, race, take, put } from 'redux-saga/effects'
import { formValueSelector } from 'redux-form/immutable'
import { Map } from 'immutable'
import _ from 'lodash'

import { error, success } from '../../../../services/toastr'

import { CONFIRM_DIALOG_ON_BUTTON_CLICK } from '../Confirm/constants'
import { FORM_KEY, FORM_FIELDS } from './components/GroupMonitoringForm/constants'
import {
    ADD_GROUP_MONITORING,
    REMOVE_GROUP_MONITORING,
    INITIALIZE_GROUP_MONITORING_DIALOG,
    INITIALIZE_GROUP_MONITORING_REQUEST_SUCCESS,
    INITIALIZE_GROUP_MONITORING_REQUEST_FAILED,
    GROUPS_LOADED,
    LOAD_GROUPS_REQUEST_FAILED,
    UPDATE_GROUP_MONITORING_REQUEST_SUCCESS,
    UPDATE_GROUP_MONITORING_REQUEST_FAILED,
    LOCALE_PREFIX,
    TEXT_ID,
} from './constants'

import { initializeGroupMonitoringRequest, groupMonitoringDataRequest, updateGroupMonitoringRequest } from './actions'
import { openDialog, closeDialog } from '../../../../services/dialog/actions'
import { loadGroups } from '../../../../scenes/Gnosus/components/EntityStack/components/StackContent/components/GroupsContent/actions'

import {
    activeGroupSelector,
    activeGroupIdSelector,
    sessionIdSelector,
    userEmailSelector,
    groupMonitoringParamsSelector,
} from './selectors'

function* addGroupMonitoringCallSaga() {
    const selector = formValueSelector(FORM_KEY)
    const currentState = yield select((state) => state)
    const params = yield select(groupMonitoringParamsSelector)
    const { recipients, sendingFrequency, sendingMethod, sendingMFrequencySelection, sendingWFrequencySelection } =
        selector(currentState, ...FORM_FIELDS)

    let frequency = 'd23'
    let mailNotifications = true
    if (sendingFrequency === 'weekly') {
        frequency = `w${sendingWFrequencySelection || '1'}23`
    } else if (sendingFrequency === 'monthly') {
        frequency = `m${sendingMFrequencySelection || '01'}23`
    } else if (sendingFrequency === 'no') {
        mailNotifications = false
    }
    const userEmail = yield select(userEmailSelector)
    let nextRecipients = recipients && recipients.toJS ? recipients.toJS() : recipients
    nextRecipients = !nextRecipients || !nextRecipients.length ? [userEmail] : nextRecipients
    const monitoringData = {
        frequency,
        notifications: mailNotifications,
        mailTo: nextRecipients.map((nextRecipient) => nextRecipient.value),
        sendInFormat: sendingMethod,
        params,
    }

    const formData = `frequency=${encodeURIComponent(frequency)}&mail_notifications=${encodeURIComponent(
        mailNotifications
    )}
        &mail_to=${encodeURIComponent(nextRecipients.map(({ value }) => value).join(','))}
        &send_as=${encodeURIComponent(sendingMethod)}&params=${params}`

    const activeGroup = yield select(activeGroupSelector)
    const queryId = activeGroup.getIn(['monitoring', 'queryId'])
    const groupIdentifier = yield select(activeGroupIdSelector)
    const sessionId = yield select(sessionIdSelector)

    const updateGroupResult = yield all({
        response: race({
            success: take(UPDATE_GROUP_MONITORING_REQUEST_SUCCESS),
            failed: take(UPDATE_GROUP_MONITORING_REQUEST_FAILED),
        }),
        request: put(updateGroupMonitoringRequest(groupIdentifier, sessionId, queryId, formData, monitoringData)),
    })

    if (updateGroupResult.response.success) {
        success({ id: `${LOCALE_PREFIX}.updateGroupMonitoringSuccess`, values: { groupName: activeGroup.get('name') } })
        yield put(loadGroups())
    } else if (updateGroupResult.response.failed) {
        error({ id: `${LOCALE_PREFIX}.updateGroupMonitoringFailed`, values: { groupName: activeGroup.get('name') } })
    }

    yield put(closeDialog('groupMonitoring'))

    return null
}

function* initializeGroupMonitoringCallSaga() {
    const groupIdentifier = yield select(activeGroupIdSelector)
    let activeGroup = yield select(activeGroupSelector)
    const sessionId = yield select(sessionIdSelector)
    const monitored = activeGroup.get('flags', Map()).get('monitoring', false)

    if (!monitored) {
        const groupInitializeResult = yield all({
            response: race({
                success: take(INITIALIZE_GROUP_MONITORING_REQUEST_SUCCESS),
                failed: take(INITIALIZE_GROUP_MONITORING_REQUEST_FAILED),
            }),
            request: put(initializeGroupMonitoringRequest(groupIdentifier, sessionId, true)),
        })

        if (groupInitializeResult.response.success) {
            const loadGroupsResult = yield all({
                response: race({
                    success: take(GROUPS_LOADED),
                    failed: take(LOAD_GROUPS_REQUEST_FAILED),
                }),
                request: put(loadGroups()),
            })

            if (loadGroupsResult.response.success) {
                activeGroup = yield select(activeGroupSelector)
                const queryId = activeGroup.getIn(['monitoring', 'queryId'], null)

                yield put(groupMonitoringDataRequest(groupIdentifier, sessionId, queryId))
                success({
                    id: `${LOCALE_PREFIX}.initGroupMonitoringSuccess`,
                    values: { groupName: activeGroup.get('name') },
                })
            } else {
                error({
                    id: `${LOCALE_PREFIX}.initGroupMonitoringFailed`,
                    values: { groupName: activeGroup.get('name') },
                })
            }
        } else if (groupInitializeResult.response.failed) {
            const limitsResponse = _.get(groupInitializeResult.response.failed, 'payload.error.res', null)

            if (limitsResponse) {
                const body = yield limitsResponse
                    .json()
                    .then((_json) => _json)
                    .catch(() => ({}))
                const code = _.get(body, 'data.code', null)
                if (code === 20002 || code === 20005) {
                    let MESSAGE_INDEX = 'membersLimitExceeded'
                    if (code === 20002) {
                        MESSAGE_INDEX = 'groupsLimitExceeded'
                    }
                    const dialogSettings = {
                        header: { textId: 'limit', icon: 'warning' },
                        question: { textId: MESSAGE_INDEX },
                        buttons: ['ok'],
                    }
                    yield put(closeDialog('groupMonitoring'))
                    yield put(openDialog('confirm', activeGroup.get('id'), dialogSettings))
                }
            }
            error({ id: `${LOCALE_PREFIX}.initGroupMonitoringFailed`, values: { groupName: activeGroup.get('name') } })
        }
    }

    return null
}

function* removeGroupMonitoringCallSaga() {
    const groupIdentifier = yield select(activeGroupIdSelector)
    const sessionId = yield select(sessionIdSelector)
    const dialogSettings = {
        header: { textId: TEXT_ID, icon: 'warning' },
        question: {
            textId: TEXT_ID,
        },
        buttons: ['yes', 'no'],
    }

    const userResponse = yield all({
        answer: race({
            delete: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('yes', groupIdentifier)),
            cancel: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('no', groupIdentifier)),
        }),
        dialog: put(openDialog('confirm', groupIdentifier, dialogSettings)),
    })

    if (userResponse.answer.delete && userResponse.answer.delete.data.type === 'yes') {
        const removeGroupMonitoringResult = yield all({
            response: race({
                success: take(INITIALIZE_GROUP_MONITORING_REQUEST_SUCCESS),
                failed: take(INITIALIZE_GROUP_MONITORING_REQUEST_FAILED),
            }),
            request: put(initializeGroupMonitoringRequest(groupIdentifier, sessionId, false)),
        })
        const activeGroup = yield select(activeGroupSelector)
        if (removeGroupMonitoringResult.response.success) {
            success({
                id: `${LOCALE_PREFIX}.removeGroupMonitoringSuccess`,
                values: { groupName: activeGroup.get('name') },
            })
        } else if (removeGroupMonitoringResult.response.failed) {
            error({
                id: `${LOCALE_PREFIX}.removeGroupMonitoringFailed`,
                values: { groupName: activeGroup.get('name') },
            })
        }
        yield put(loadGroups())
    }

    return null
}

export default function* groupMonitoringSaga() {
    yield all([
        takeEvery(ADD_GROUP_MONITORING, addGroupMonitoringCallSaga),
        takeEvery(INITIALIZE_GROUP_MONITORING_DIALOG, initializeGroupMonitoringCallSaga),
        takeEvery(REMOVE_GROUP_MONITORING, removeGroupMonitoringCallSaga),
    ])
}
